import { differenceInCalendarDays, format, subDays, subMonths } from "date-fns";

export type OmnichannelTabId =
  | "overview"
  | "sales"
  | "inventory"
  | "customers"
  | "financials";

export type OmnichannelSource = "zid" | "odoo";
export type OmnichannelChannel = "online" | "physical";
export type OmnichannelStatus =
  | "completed"
  | "pending"
  | "refunded"
  | "cancelled";

export interface OmnichannelTabItem {
  id: OmnichannelTabId;
  label: string;
}

export interface OmnichannelOrder {
  id: string;
  orderNumber: string;
  source: OmnichannelSource;
  channel: OmnichannelChannel;
  customerName: string;
  customerNameAr: string;
  customerEmail: string;
  items: number;
  total: number;
  tax: number;
  status: OmnichannelStatus;
  date: string;
}

export interface OmnichannelProduct {
  id: string;
  name: string;
  nameAr: string;
  sku: string;
  category: string;
  categoryAr: string;
  price: number;
  cost: number;
  zidStock: number;
  odooStock: number;
  totalStock: number;
  reorderPoint: number;
  status: "in_stock" | "low_stock" | "out_of_stock";
}

export interface OmnichannelCustomer {
  id: string;
  name: string;
  nameAr: string;
  email: string;
  source: OmnichannelSource;
  totalOrders: number;
  totalSpent: number;
  avgOrderValue: number;
  firstOrder: string;
  lastOrder: string;
  type: "new" | "returning";
  cltv: number;
}

export interface DailySalesPoint {
  date: string;
  zid: number;
  odoo: number;
}

export interface MonthlyPoint {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export interface DistributionPoint {
  label: string;
  value: number;
}

export interface ChannelPoint extends DistributionPoint {
  channel: OmnichannelChannel;
  percentage: number;
}

const NOW = new Date("2026-04-02T00:00:00");

export interface OmnichannelDateRange {
  startDate: string;
  endDate: string;
}

const rangeSpanDays = (range: OmnichannelDateRange): number =>
  Math.max(0, differenceInCalendarDays(new Date(range.endDate), new Date(range.startDate)));

export const getPreviousDateRange = (range: OmnichannelDateRange): OmnichannelDateRange => {
  const spanDays = rangeSpanDays(range);
  const previousEnd = subDays(new Date(range.startDate), 1);
  const previousStart = subDays(previousEnd, spanDays);
  return {
    startDate: format(previousStart, "yyyy-MM-dd"),
    endDate: format(previousEnd, "yyyy-MM-dd"),
  };
};

export const computeChangePercent = (current: number, previous: number): number | null => {
  if (previous === 0) {
    return current === 0 ? 0 : null;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const rangeSeedOffset = (range: OmnichannelDateRange): number => {
  const key = `${range.startDate}|${range.endDate}`;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 100000;
};

interface LocalizedName {
  name: string;
  nameAr: string;
}

const PRODUCTS: LocalizedName[] = [
  { name: "Ajwa Dates Premium Box", nameAr: "تمر عجوة فاخر - علبة" },
  { name: "Black Crepe Abaya", nameAr: "عباية كريب سوداء" },
  { name: "Cambodian Oud Oil", nameAr: "دهن عود كمبودي" },
  { name: "Saudi Arabica Coffee Beans", nameAr: "حبوب قهوة عربية سعودية" },
  { name: "Majlis Floor Cushion Set", nameAr: "طقم مساند مجلس" },
  { name: "Sukkari Dates 1kg", nameAr: "تمر سكري - كيلو" },
  { name: "Embroidered Open Abaya", nameAr: "عباية مفتوحة مطرزة" },
  { name: "Bakhoor Maamoul Pack", nameAr: "بخور معمول" },
  { name: "Saudi Coffee Dallah", nameAr: "دلة قهوة سعودية" },
  { name: "Sadu Patterned Rug", nameAr: "سجادة بنقشة السدو" },
  { name: "Khudri Dates Gift Tray", nameAr: "صينية تمر خضري للإهداء" },
  { name: "Linen Summer Abaya", nameAr: "عباية كتان صيفية" },
  { name: "Taif Rose Perfume", nameAr: "عطر ورد الطائف" },
  { name: "Cardamom & Saffron Blend", nameAr: "خلطة هيل وزعفران" },
  { name: "Arabic Tea Glass Set", nameAr: "طقم استكانات شاي عربي" },
  { name: "Date Maamoul Sweets Box", nameAr: "علبة معمول بالتمر" },
  { name: "Butterfly Sleeve Abaya", nameAr: "عباية بأكمام فراشة" },
  { name: "Oud & Amber Body Mist", nameAr: "معطر جسم عود وعنبر" },
  { name: "Green Cardamom 250g", nameAr: "هيل أخضر ٢٥٠ جرام" },
  { name: "Incense Burner Mabkhara", nameAr: "مبخرة" },
];

const CATEGORIES: LocalizedName[] = [
  { name: "Dates & Sweets", nameAr: "تمور وحلويات" },
  { name: "Abayas & Fashion", nameAr: "عبايات وأزياء" },
  { name: "Fragrance & Oud", nameAr: "عطور وعود" },
  { name: "Coffee & Tea", nameAr: "قهوة وشاي" },
  { name: "Home & Majlis", nameAr: "المنزل والمجلس" },
];

const CUSTOMERS: LocalizedName[] = [
  { name: "Mohammed Al-Otaibi", nameAr: "محمد العتيبي" },
  { name: "Fatimah Al-Zahrani", nameAr: "فاطمة الزهراني" },
  { name: "Abdullah Al-Qahtani", nameAr: "عبدالله القحطاني" },
  { name: "Noura Al-Harbi", nameAr: "نورة الحربي" },
  { name: "Khalid Al-Ghamdi", nameAr: "خالد الغامدي" },
  { name: "Sarah Al-Dosari", nameAr: "سارة الدوسري" },
  { name: "Faisal Al-Shehri", nameAr: "فيصل الشهري" },
  { name: "Aisha Al-Malki", nameAr: "عائشة المالكي" },
  { name: "Omar Al-Anazi", nameAr: "عمر العنزي" },
  { name: "Latifah Al-Mutairi", nameAr: "لطيفة المطيري" },
  { name: "Saad Al-Amri", nameAr: "سعد العمري" },
  { name: "Reem Al-Subaie", nameAr: "ريم السبيعي" },
  { name: "Yousef Al-Zahrani", nameAr: "يوسف الزهراني" },
  { name: "Hind Al-Rashidi", nameAr: "هند الرشيدي" },
  { name: "Turki Al-Juhani", nameAr: "تركي الجهني" },
  { name: "Maha Al-Qahtani", nameAr: "مها القحطاني" },
  { name: "Nasser Al-Harthy", nameAr: "ناصر الحارثي" },
  { name: "Amal Al-Otaibi", nameAr: "أمل العتيبي" },
  { name: "Bandar Al-Saud", nameAr: "بندر آل سعود" },
  { name: "Layla Al-Yami", nameAr: "ليلى اليامي" },
  { name: "Hamad Al-Tamimi", nameAr: "حمد التميمي" },
  { name: "Munira Al-Ghamdi", nameAr: "منيرة الغامدي" },
  { name: "Talal Al-Hajri", nameAr: "طلال الهاجري" },
  { name: "Sara Al-Balawi", nameAr: "سارة البلوي" },
  { name: "Ibrahim Al-Sulaiman", nameAr: "إبراهيم السليمان" },
];

export const OMNICHANNEL_TAB_ITEMS: OmnichannelTabItem[] = [
  { id: "overview", label: "Overview" },
  { id: "sales", label: "Sales" },
  { id: "inventory", label: "Inventory" },
  { id: "customers", label: "Customers" },
  { id: "financials", label: "Financials" },
];

export const generateOmnichannelProducts = (): OmnichannelProduct[] =>
  PRODUCTS.map((product, index) => {
    const seed = index * 7 + 3;
    const price = Math.round((15 + seededRandom(seed) * 85) * 100) / 100;
    const cost = Math.round(
      price * (0.3 + seededRandom(seed + 1) * 0.3) * 100
    ) / 100;
    const zidStock = Math.floor(seededRandom(seed + 2) * 200);
    const odooStock = Math.floor(seededRandom(seed + 3) * 150);
    const totalStock = zidStock + odooStock;
    const reorderPoint = Math.floor(15 + seededRandom(seed + 4) * 35);

    return {
      id: `prod-${index + 1}`,
      name: product.name,
      nameAr: product.nameAr,
      sku: `SKU-${String(1000 + index)}`,
      category: CATEGORIES[index % CATEGORIES.length].name,
      categoryAr: CATEGORIES[index % CATEGORIES.length].nameAr,
      price,
      cost,
      zidStock,
      odooStock,
      totalStock,
      reorderPoint,
      status:
        totalStock === 0
          ? "out_of_stock"
          : totalStock < reorderPoint
            ? "low_stock"
            : "in_stock",
    };
  });

export const generateOmnichannelOrders = (
  range: OmnichannelDateRange
): OmnichannelOrder[] => {
  const orders: OmnichannelOrder[] = [];
  const sources: OmnichannelSource[] = ["zid", "odoo"];
  const channels: OmnichannelChannel[] = ["online", "physical"];
  const statuses: OmnichannelStatus[] = [
    "completed",
    "completed",
    "completed",
    "completed",
    "pending",
    "refunded",
    "cancelled",
  ];

  const rangeEnd = new Date(range.endDate);
  const spanDays = rangeSpanDays(range);
  const orderCount = Math.max(20, Math.min(320, (spanDays + 1) * 8));
  const seedOffset = rangeSeedOffset(range);

  for (let index = 0; index < orderCount; index += 1) {
    const seed = index * 13 + 7 + seedOffset;
    const date = format(
      subDays(rangeEnd, Math.floor(seededRandom(seed) * (spanDays + 1))),
      "yyyy-MM-dd"
    );
    const source = sources[Math.floor(seededRandom(seed + 1) * sources.length)];
    const channel =
      source === "odoo" && seededRandom(seed + 5) > 0.45
        ? "physical"
        : channels[Math.floor(seededRandom(seed + 2) * channels.length)];
    const total = Math.round((25 + seededRandom(seed + 3) * 520) * 100) / 100;
    const customerIdx = Math.floor(seededRandom(seed + 4) * CUSTOMERS.length);
    const customer = CUSTOMERS[customerIdx];

    orders.push({
      id: `ord-${String(index + 1).padStart(4, "0")}`,
      orderNumber: `#${10000 + index}`,
      source,
      channel,
      customerName: customer.name,
      customerNameAr: customer.nameAr,
      customerEmail: `${customer.name
        .toLowerCase()
        .replace(/[' ]/g, ".")}@example.com`,
      items: 1 + Math.floor(seededRandom(seed + 6) * 5),
      total,
      tax: Math.round(total * 0.08 * 100) / 100,
      status: statuses[Math.floor(seededRandom(seed + 7) * statuses.length)],
      date,
    });
  }

  return orders.sort((a, b) => b.date.localeCompare(a.date));
};

export const generateOmnichannelCustomers = (
  range: OmnichannelDateRange
): OmnichannelCustomer[] => {
  const rangeEnd = new Date(range.endDate);
  const spanDays = rangeSpanDays(range);
  const seedOffset = rangeSeedOffset(range);

  return CUSTOMERS.map((customer, index) => {
    const seed = index * 17 + 11 + seedOffset;
    const totalOrders = 1 + Math.floor(seededRandom(seed) * 30);
    const avgOrderValue = Math.round(
      (30 + seededRandom(seed + 1) * 170) * 100
    ) / 100;
    const totalSpent = Math.round(totalOrders * avgOrderValue * 100) / 100;
    const lastOrderOffset = Math.floor(seededRandom(seed + 3) * (spanDays + 1));
    const firstOrderOffset = Math.min(
      spanDays,
      lastOrderOffset + Math.floor(seededRandom(seed + 2) * (spanDays - lastOrderOffset + 1))
    );

    return {
      id: `cust-${index + 1}`,
      name: customer.name,
      nameAr: customer.nameAr,
      email: `${customer.name.toLowerCase().replace(/[' ]/g, ".")}@example.com`,
      source: seededRandom(seed + 4) > 0.5 ? "zid" : "odoo",
      totalOrders,
      totalSpent,
      avgOrderValue,
      firstOrder: format(subDays(rangeEnd, firstOrderOffset), "yyyy-MM-dd"),
      lastOrder: format(subDays(rangeEnd, lastOrderOffset), "yyyy-MM-dd"),
      type: totalOrders > 3 ? "returning" : "new",
      cltv: Math.round(
        totalSpent * (1 + seededRandom(seed + 5) * 0.5) * 100
      ) / 100,
    };
  });
};

export const getOverviewSalesTrend = (
  range: OmnichannelDateRange
): DailySalesPoint[] => {
  const data: DailySalesPoint[] = [];
  const rangeEnd = new Date(range.endDate);
  const days = rangeSpanDays(range);

  for (let index = days; index >= 0; index -= 1) {
    const seed = index * 23 + 5;
    const day = subDays(rangeEnd, index).getDay();
    const weekendFactor = day === 0 || day === 6 ? 1.3 : 1;
    const trendFactor = 1 + (days - index) * 0.003;

    const zid = Math.round(
      (800 + seededRandom(seed) * 2200) * weekendFactor * trendFactor
    );
    const odoo = Math.round(
      (500 + seededRandom(seed + 1) * 1500) * weekendFactor * trendFactor
    );

    data.push({
      date: format(subDays(rangeEnd, index), "yyyy-MM-dd"),
      zid,
      odoo,
    });
  }

  return data;
};

export const getChannelDistribution = (
  orders: OmnichannelOrder[]
): ChannelPoint[] => {
  const map: Record<OmnichannelChannel, { value: number; label: string }> = {
    online: { value: 0, label: "Online Store" },
    physical: { value: 0, label: "Physical Store" },
  };

  orders
    .filter(order => order.status === "completed")
    .forEach(order => {
      map[order.channel].value += order.total;
    });

  const total = Object.values(map).reduce((sum, item) => sum + item.value, 0);

  return Object.entries(map).map(([channel, item]) => ({
    channel: channel as OmnichannelChannel,
    label: item.label,
    value: Math.round(item.value),
    percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
  }));
};

export const getFinancialSummary = (orders: OmnichannelOrder[]) => {
  const completed = orders.filter(order => order.status === "completed");
  const refunded = orders.filter(order => order.status === "refunded");

  const revenue = completed.reduce((sum, order) => sum + order.total, 0);
  const taxes = completed.reduce((sum, order) => sum + order.tax, 0);
  const refunds = refunded.reduce((sum, order) => sum + order.total, 0);
  const cogs = revenue * 0.42;
  const expenses = revenue * 0.18;

  return {
    revenue: Math.round(revenue),
    cogs: Math.round(cogs),
    grossProfit: Math.round(revenue - cogs),
    taxes: Math.round(taxes),
    expenses: Math.round(expenses),
    netProfit: Math.round(revenue - cogs - taxes - expenses - refunds),
    refunds: Math.round(refunds),
  };
};

export const getOverviewKpis = (
  orders: OmnichannelOrder[],
  customers: OmnichannelCustomer[],
  products: OmnichannelProduct[]
) => {
  const completed = orders.filter(order => order.status === "completed");
  const revenue = completed.reduce((sum, order) => sum + order.total, 0);
  const avgCltv =
    customers.reduce((sum, customer) => sum + customer.cltv, 0) / customers.length;
  const inventoryValue = products.reduce(
    (sum, product) => sum + product.totalStock * product.cost,
    0
  );

  return {
    totalRevenue: Math.round(revenue),
    totalOrders: completed.length,
    activeCustomers: customers.length,
    avgOrderValue: Math.round(revenue / Math.max(completed.length, 1)),
    inventoryValue: Math.round(inventoryValue),
    avgCltv: Math.round(avgCltv),
  };
};

export const getMonthlyFinancials = (
  orders: OmnichannelOrder[]
): MonthlyPoint[] => {
  const months: Record<string, MonthlyPoint> = {};
  const latestOrderDate = orders.reduce<Date | null>((latest, order) => {
    const orderDate = new Date(order.date);
    return !latest || orderDate > latest ? orderDate : latest;
  }, null);
  const anchor = latestOrderDate ?? NOW;

  // Stepping back in 30-day jumps drifts against real month lengths: over 12 steps it
  // duplicates some months and skips others entirely (February first), so any order in a
  // skipped month never lands in a bucket. Step by calendar months instead.
  for (let index = 11; index >= 0; index -= 1) {
    const month = format(subMonths(anchor, index), "yyyy-MM");
    months[month] = { month, revenue: 0, expenses: 0, profit: 0 };
  }

  orders
    .filter(order => order.status === "completed")
    .forEach(order => {
      const month = format(new Date(order.date), "yyyy-MM");
      if (!months[month]) {
        return;
      }
      months[month].revenue += order.total;
      months[month].expenses += order.total * 0.6;
      months[month].profit += order.total * 0.4;
    });

  return Object.values(months).map(item => ({
    ...item,
    revenue: Math.round(item.revenue),
    expenses: Math.round(item.expenses),
    profit: Math.round(item.profit),
  }));
};

export const getCltvBuckets = (
  customers: OmnichannelCustomer[]
): DistributionPoint[] => [
  { label: "SAR 0-200", value: customers.filter(customer => customer.cltv < 200).length },
  {
    label: "SAR 200-500",
    value: customers.filter(
      customer => customer.cltv >= 200 && customer.cltv < 500
    ).length,
  },
  {
    label: "SAR 500-1k",
    value: customers.filter(
      customer => customer.cltv >= 500 && customer.cltv < 1000
    ).length,
  },
  {
    label: "SAR 1k-2k",
    value: customers.filter(
      customer => customer.cltv >= 1000 && customer.cltv < 2000
    ).length,
  },
  { label: "SAR 2k+", value: customers.filter(customer => customer.cltv >= 2000).length },
];

export const getCustomerSegmentation = (
  customers: OmnichannelCustomer[]
): DistributionPoint[] => [
  {
    label: "New",
    value: customers.filter(customer => customer.type === "new").length,
  },
  {
    label: "Returning",
    value: customers.filter(customer => customer.type === "returning").length,
  },
];

export const getTaxBreakdown = (taxes: number): DistributionPoint[] => [
  { label: "Sales Tax", value: Math.round(taxes * 0.65) },
  { label: "VAT", value: Math.round(taxes * 0.25) },
  { label: "Other", value: Math.round(taxes * 0.1) },
];

export const paginateRows = <T,>(rows: T[], page: number, pageSize = 10) =>
  rows.slice((page - 1) * pageSize, page * pageSize);
